/**
 * Test Drive — Dealer Dashboard Page (iter133a — Phase 1)
 *
 * Two-gate rule enforced on this screen:
 *   - Master gate off  → show "Not entitled" panel (dealer contacts rep)
 *   - Master gate on   → show the dealer's switch + money-guardrail
 *                        summary (read-only — set by master)
 *
 * Later phases will grow this page with:
 *   - popup design/copy controls (dealer_config)
 *   - a redemption page link
 *   - an appointments list
 *
 * For Phase 1 we only need the dealer-side switch and enough context so
 * the popup gate can be exercised end-to-end.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Car, Gift, Lock, Info, Loader2, LayoutDashboard, Sidebar, Rows } from "lucide-react";
import TemplateMiniPreview from "../components/TemplateMiniPreview";

const TEMPLATES = [
  { key: "A", label: "Native card", desc: "Compact centered modal — blends into the site.", icon: LayoutDashboard },
  { key: "B", label: "Slide-up bar", desc: "Slim dock at the bottom, expands on tap.",       icon: Sidebar },
  { key: "C", label: "Split panel", desc: "Two-column reward hero + form on the right.",     icon: Rows },
];

export default function TestDrivePage() {
  const { apiFetch } = useAuth();
  const { toast } = useToast();
  const [entitlement, setEntitlement] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [entRes, prevRes] = await Promise.all([
        apiFetch("/dealer/products", { method: "GET" }),
        apiFetch("/dealer/test-drive/preview", { method: "GET" }).catch(() => ({ data: null })),
      ]);
      const list = entRes?.data || [];
      const td = list.find((p) => p.code === "test_drive");
      setEntitlement(td || null);
      setPreview(prevRes?.data || null);
    } catch (e) {
      console.error("[TestDrive] load failed", e);
      toast?.({ title: "Failed to load Test Drive", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [apiFetch, toast]);

  useEffect(() => { load(); }, [load]);

  const toggleDealerSwitch = async (next) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await apiFetch("/dealer/products/test_drive", {
        method: "PATCH",
        data: { dealer_active: next },
      });
      setEntitlement(res?.data || entitlement);
      toast?.({
        title: next ? "Test Drive popup is LIVE" : "Test Drive popup paused",
        variant: "default",
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Update failed";
      toast?.({ title: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600" data-testid="test-drive-loading">
        <Loader2 className="animate-spin" size={16} /> Loading Test Drive…
      </div>
    );
  }

  // No entitlement row at all — treat as not-entitled.
  if (!entitlement) {
    return <NotEntitled />;
  }

  const { master_active, dealer_active, master_config = {}, dealer_config = {} } = entitlement;
  // iter138 P1 — face value MUST come from the active campaign row
  // (test_drive_campaigns.denomination_cents), not a stale master
  // config field. This is the same value the live popup renders, so
  // dealer dashboard, toggle text, and popup can never disagree.
  const faceValueDollars =
    typeof entitlement.active_campaign_denomination_cents === "number"
      ? (entitlement.active_campaign_denomination_cents / 100).toFixed(0)
      : null;
  const activeTemplate = String(dealer_config.active_template || "A").toUpperCase();
  // iter138 S2 — surface the system-suspended state so the dealer sees
  // "Paused — awaiting gift card inventory" rather than an ambiguous "off".
  const systemStatus = entitlement.system_status || "active";
  const systemSuspended = systemStatus !== "active";

  const switchTemplate = async (nextKey) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await apiFetch("/dealer/products/test_drive", {
        method: "PATCH",
        data: { dealer_config: { active_template: nextKey } },
      });
      setEntitlement(res?.data || entitlement);
      // Refresh preview to reflect the newly-active template (used for
      // the "Active on your site" pill and any master-side branding
      // changes that landed since the last render).
      apiFetch("/dealer/test-drive/preview", { method: "GET" })
        .then((r) => setPreview(r?.data || preview))
        .catch(() => {});
      toast?.({ title: `Template switched to ${nextKey}` });
    } catch (e) {
      toast?.({ title: e?.response?.data?.detail || "Update failed", variant: "destructive" });
    } finally { setSaving(false); }
  };

  if (!master_active) {
    return <NotEntitled />;
  }

  return (
    <div className="p-6 max-w-3xl space-y-6" data-testid="test-drive-page">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center">
          <Car size={18} className="text-red-600" />
        </div>
        <div>
          <h1 className="text-[20px] font-semibold text-gray-900 leading-tight">Test Drive</h1>
          <p className="text-[13px] text-gray-500">
            Reward customers with a gift card for coming in to test drive a vehicle.
          </p>
        </div>
      </header>

      {/* Master-set guardrails — read-only summary. */}
      <section
        className="bg-white border border-gray-200 rounded-xl p-5 space-y-3"
        data-testid="test-drive-guardrails"
      >
        <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-800">
          <Lock size={14} /> Program details set by Verified Demand
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
          <div>
            <dt className="text-gray-500">Gift card face value</dt>
            <dd className="font-semibold text-gray-900" data-testid="td-face-value">
              {faceValueDollars ? `$${faceValueDollars}` : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Per-customer cap</dt>
            <dd className="font-semibold text-gray-900" data-testid="td-per-person-cap">
              {master_config.per_person_cap ?? "—"}
            </dd>
          </div>
        </dl>
        <p className="text-[12px] text-gray-500 flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 flex-shrink-0" />
          These values control how much of our budget your program spends. Contact your Verified
          Demand rep to change them.
        </p>
      </section>

      {/* iter138 S2 — Paused banner. When the campaign is
          system-suspended (e.g. drained to zero), tell the dealer
          exactly why so they don't think their own toggle is broken. */}
      {systemSuspended && (
        <section
          className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3"
          data-testid="test-drive-system-paused-banner"
        >
          <Info size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-[13px] font-semibold text-amber-900">
              Paused — awaiting gift card inventory
            </div>
            <div className="text-[12px] text-amber-800 mt-1">
              {entitlement.system_status_reason ||
                "Your Test Drive popup is temporarily paused. Verified Demand is loading more gift cards; the popup will resume automatically once inventory is available and reactivated."}
              {" "}
              Your on/off setting has been preserved.
            </div>
          </div>
        </section>
      )}

      {/* Dealer switch — the only field the dealer can toggle in Phase 1. */}
      <section
        className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-4"
        data-testid="test-drive-switch-section"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
            <Gift size={16} className="text-red-500" />
            Test Drive popup
          </div>
          <p className="text-[12.5px] text-gray-500 mt-0.5">
            When ON, visitors to your site see a popup offering a
            {faceValueDollars ? ` $${faceValueDollars} gift card` : " gift card"} to come in for a
            test drive.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={dealer_active}
          onClick={() => toggleDealerSwitch(!dealer_active)}
          disabled={saving}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-50 ${
            dealer_active ? "bg-red-600" : "bg-gray-300"
          }`}
          data-testid="test-drive-dealer-switch"
        >
          <span
            aria-hidden
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
              dealer_active ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </section>

      {/* iter133c — Dealer picks only the frame variant. Copy, colors,
          amount, consent, and artwork stay master-locked. */}
      <section
        className="bg-white border border-gray-200 rounded-xl p-5"
        data-testid="test-drive-template-picker"
      >
        <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-800 mb-3">
          <LayoutDashboard size={14} /> Popup style
        </div>
        <p className="text-[12px] text-gray-500 mb-4">
          Pick the layout your visitors see. Amount, copy, and colors stay set by Verified Demand
          and are identical across all three.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TEMPLATES.map(({ key, label, desc, icon: Icon }) => {
            const isActive = activeTemplate === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => switchTemplate(key)}
                disabled={saving}
                aria-pressed={isActive}
                data-testid={`test-drive-template-${key.toLowerCase()}`}
                className={`text-left border-2 rounded-lg p-3 transition-all disabled:opacity-50 ${
                  isActive
                    ? "border-red-500 bg-red-50 ring-2 ring-red-100"
                    : "border-gray-200 hover:border-gray-400 bg-white"
                }`}
              >
                {/* iter133d — real mini-preview with actual branding + amount + artwork */}
                <div className="mb-3">
                  <TemplateMiniPreview template={key} preview={preview} />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} className={isActive ? "text-red-600" : "text-gray-500"} />
                  <span className="text-[12.5px] font-semibold text-gray-900">
                    Template {key} — {label}
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 leading-snug">{desc}</p>
                {isActive && (
                  <p className="text-[10.5px] text-red-600 font-semibold mt-1.5">Active on your site</p>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Placeholders — Phase 2/3 will flesh these out. */}
      <section
        className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-5 space-y-2 text-[13px] text-gray-600"
        data-testid="test-drive-phase-hints"
      >
        <p className="font-semibold text-gray-800">Coming soon</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Popup design & copy controls</li>
          <li>Your redemption page (staff enters the customer&apos;s claim ticket)</li>
          <li>Appointments list</li>
        </ul>
      </section>
    </div>
  );
}


function NotEntitled() {
  return (
    <div className="p-6 max-w-3xl" data-testid="test-drive-not-entitled">
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <Car size={18} className="text-gray-500" />
          </div>
          <h1 className="text-[18px] font-semibold text-gray-900">Test Drive</h1>
        </div>
        <p className="text-[13px] text-gray-600">
          The Test Drive gift-card program hasn&apos;t been enabled for your store yet. Reach out to
          your Verified Demand account manager to get started.
        </p>
      </div>
    </div>
  );
}
